// Makkerspillets logbog (IGN-løkke runde 4): hvem gjorde hvad, så resultatskærmen kan rose begge.
// Tæller hændelser på klienten; hver handling bærer selv, hvem der gjorde den (spor "sandhed").

import type { ActionKind, PlayerId, SimEvent } from '../sim';
import { UNDO_TICKS } from '../sim/grid';
import { DRIFT } from '../sim/roles';
import { h } from './dom';

type Key = 'duo' | 'lines' | 'stations' | 'storage' | 'upgrades' | 'masts' | 'held' | 'powers' | 'crews' | 'shooed' | 'cards';

/**
 * Så længe efter en handling (ticks) tæller en redning med som din (IGN-løkke 2 runde 2: "Driftsvagten
 * får ikke anerkendelse"). 15 s: Norges hjælp varer 20 s, Lynreservens 10 s.
 */
export const RESCUE_WINDOW = 150;

const KIND: Partial<Record<SimEvent['t'], { key: Key; kind: ActionKind }>> = {
  station_upgraded: { key: 'stations', kind: 'upgrade_station' },
  battery_built: { key: 'storage', kind: 'build_battery' },
  flywheel_built: { key: 'storage', kind: 'build_flywheel' },
  line_upgraded: { key: 'upgrades', kind: 'upgrade_line' },
  mast_reinforced: { key: 'masts', kind: 'reinforce_mast' },
  crew_dispatched: { key: 'crews', kind: 'dispatch_crew' },
  threat_shooed: { key: 'shooed', kind: 'shoo_threat' },
};

/** Ental og flertal pr. tæller, i den rækkefølge de står på skærmen. */
const WORDS: [Key, string, string][] = [
  ['duo', 'fælles lyn klaret sammen', 'fælles lyn klaret sammen'],
  ['lines', 'ledning bygget', 'ledninger bygget'],
  ['stations', 'station opgraderet', 'stationer opgraderet'],
  ['storage', 'lager bygget', 'lagre bygget'],
  ['upgrades', 'ledning opgraderet', 'ledninger opgraderet'],
  ['masts', 'mast forstærket', 'master forstærket'],
  ['held', 'brud undgået med en forstærket mast', 'brud undgået med forstærkede master'],
  ['powers', 'nødknap brugt', 'nødknapper brugt'],
  ['crews', 'montør sendt ud', 'montører sendt ud'],
  ['shooed', 'gæst jaget væk', 'gæster jaget væk'],
  ['cards', 'kort besvaret', 'kort besvaret'],
];

/** Hvem logbogen giver æren for en hændelse (null: ingen). */
export function creditOf(e: SimEvent): PlayerId | null {
  if (e.t === 'line_built') return e.by;
  if (e.t === 'card_answered') return e.auto ? null : (e.by ?? null);
  // Kun den, der faktisk gjorde det: en montør, der selv kører ud, eller en mast, der bliver færdig,
  // er ingens (runde 5: "logbogen giver Driftsvagten ting, ingen gjorde").
  if (!KIND[e.t]) return null;
  const by = (e as { by?: unknown }).by;
  return by === 'p1' || by === 'p2' ? by : null;
}

/** Tællerens nøgle for en hændelse. */
function keyOf(e: SimEvent): Key | null {
  if (e.t === 'line_built') return 'lines';
  if (e.t === 'card_answered') return 'cards';
  return KIND[e.t]?.key ?? null;
}

const NAME: Record<PlayerId, string> = { p1: 'Planlægger', p2: 'Driftsvagt' };

/** Det, der står, når en af jer ikke fik noget talt med. */
const QUIET: Record<PlayerId, string> = { p1: 'Nettet holdt uden nye byggerier.', p2: 'En rolig vagt: ingen storme at tage.' };

export class TeamLog {
  private n: Record<PlayerId, Partial<Record<Key, number>>> = { p1: {}, p2: {} };
  /** Nye ledninger, der stadig kan fortrydes: en fortrudt ledning er ikke bygget (spor "coop"). */
  private fresh = new Map<string, { by: PlayerId; tick: number }>();
  /** Seneste tick, hver af jer gjorde noget, der kan redde en by. */
  private last: Record<PlayerId, number> = { p1: -Infinity, p2: -Infinity };
  /** Hvem forstærkede masterne på hver ledning: et brud, masten tog, er hendes. */
  private mastBy = new Map<string, PlayerId>();
  /** Byer, hver af jer reddede i sidste øjeblik: id og antal gange. */
  private saved: Record<PlayerId, Map<string, number>> = { p1: new Map(), p2: new Map() };
  /** Seneste opkald til Norge, og hvor mange der kom i tide (en redning, mens hjælpen var på vej). */
  private norway: { tick: number; by: PlayerId; counted: boolean } | null = null;
  private inTime: Record<PlayerId, number> = { p1: 0, p2: 0 };

  reset(): void {
    this.n = { p1: {}, p2: {} };
    this.fresh.clear();
    this.last = { p1: -Infinity, p2: -Infinity };
    this.mastBy.clear();
    this.saved = { p1: new Map(), p2: new Map() };
    this.norway = null;
    this.inTime = { p1: 0, p2: 0 };
  }

  private add(p: PlayerId, key: Key): void {
    this.n[p][key] = (this.n[p][key] ?? 0) + 1;
  }

  events(evs: readonly SimEvent[]): void {
    for (const e of evs) {
      if (e.t === 'line_built') this.fresh.set(e.line, { by: e.by, tick: e.tick });
      if (e.t === 'line_removed') {
        const f = this.fresh.get(e.line);
        this.fresh.delete(e.line);
        if (f && e.tick - f.tick <= UNDO_TICKS) this.n[f.by].lines = Math.max(0, (this.n[f.by].lines ?? 1) - 1);
        continue;
      }
      // Det fælles lyn er begges: ingen af jer kunne klare det alene.
      if (e.t === 'duo_won') {
        this.add('p1', 'duo');
        this.add('p2', 'duo');
        continue;
      }
      // Nødknapperne er Driftsvagtens på en bane med roller (src/sim/roles.ts).
      if (e.t === 'power_used') {
        this.add(DRIFT, 'powers');
        this.last[DRIFT] = e.tick;
        if (e.power === 'norway') this.norway = { tick: e.tick, by: DRIFT, counted: false };
        continue;
      }
      if (e.t === 'line_saved' && e.by === 'reinforced') {
        const by = this.mastBy.get(e.line);
        if (by) this.add(by, 'held');
        continue;
      }
      if (e.t === 'rescue') {
        // Redningen er dens, der handlede lige før: begges, hvis I begge gjorde noget.
        for (const p of ['p1', 'p2'] as const) {
          if (e.tick - this.last[p] > RESCUE_WINDOW) continue;
          this.saved[p].set(e.node, (this.saved[p].get(e.node) ?? 0) + 1);
        }
        const call = this.norway;
        if (call && !call.counted && e.tick - call.tick <= RESCUE_WINDOW) {
          call.counted = true;
          this.inTime[call.by]++;
        }
        continue;
      }
      const who = creditOf(e);
      const key = keyOf(e);
      if (who && key) {
        this.add(who, key);
        this.last[who] = e.tick;
      }
      if (e.t === 'mast_reinforced' && who) this.mastBy.set(e.line, who);
    }
  }

  /** De konkrete øjeblikke: "Reddede Silkeborg 2 gange", "Ringede til Norge i tide". Højst tre. */
  moments(p: PlayerId, name: (id: string) => string): string[] {
    const out = [...this.saved[p]]
      .sort((a, b) => b[1] - a[1])
      .map(([id, k]) => `Reddede ${name(id) || 'en by'}${k > 1 ? ` ${k} gange` : ''}`);
    const calls = this.inTime[p];
    if (calls > 0) out.unshift(`Ringede til Norge i tide${calls > 1 ? ` ${calls} gange` : ''}`);
    return out.slice(0, 3);
  }

  /** Det, spilleren fik gjort: antal og ord, i skærmens rækkefølge. */
  rows(p: PlayerId): [number, string][] {
    return WORDS.filter(([k]) => (this.n[p][k] ?? 0) > 0).map(([k, one, many]) => {
      const v = this.n[p][k] ?? 0;
      return [v, v === 1 ? one : many];
    });
  }

  /**
   * "Jeres vagt": to kolonner med det, hver af jer fik gjort, øverst de byer, I reddede.
   * `other` navngiver makkeren (Volt i solo); `name` slår en bys navn op.
   */
  view(you: PlayerId, name: (id: string) => string = () => '', other?: string): HTMLElement {
    const col = (p: PlayerId): HTMLElement => {
      const big = this.moments(p, name).map((m) => h('li', { class: 'moment' }, m));
      const rows = this.rows(p).map(([v, w]) => h('li', {}, h('b', {}, String(v)), ` ${w}`));
      const who = p === you ? `${NAME[p]} (dig)` : other ? `${other} (${NAME[p]})` : NAME[p];
      return h(
        'div',
        { class: `team-col ${p}` },
        h('h3', {}, who),
        big.length + rows.length > 0 ? h('ul', {}, ...big, ...rows) : h('p', {}, QUIET[p]),
      );
    };
    return h('section', { class: 'team-log', 'aria-label': 'Jeres vagt' }, h('h2', { class: 'section' }, 'Jeres vagt'), h('div', { class: 'team-cols' }, col('p1'), col('p2')));
  }
}
